/**
 * The piece registry — the single source of truth for what pieces exist, what
 * they cost, how they move, and how they render. To add a fairy piece you add
 * one entry here (and, if it needs novel geometry, a pattern in movement.ts);
 * the engine, deck builder, and UI all read from this table.
 */
import {
  ALL_DIRECTIONS,
  CAMEL_OFFSETS,
  DIAGONAL,
  KNIGHT_OFFSETS,
  ORTHOGONAL,
  farmerMovement,
  forwardSlider,
  leaper,
  noMovement,
  pawnMovement,
  slider,
  type MovementPattern,
} from "./movement";
import type { CardTier, PieceTypeId, RoyalRole } from "./types";

export interface PieceDef {
  readonly id: PieceTypeId;
  readonly name: string;
  /** Deck-building cost in points. Royals are free and not deck-buildable. */
  readonly cost: number;
  /** Material value for scoring/AI/display. */
  readonly value: number;
  /** Unicode glyph used by the board renderer. */
  readonly glyph: string;
  readonly movement: MovementPattern;
  /** Royalty role, if any (drives the win condition). */
  readonly royal?: RoyalRole;
  /** Whether the piece can be bought into a deck and deployed from hand. */
  readonly deployable: boolean;
  /** What a pawn-like piece becomes on reaching the far rank. */
  readonly promotesTo?: PieceTypeId;
  /** Deploy onto ANY empty square, not just the owner's home ranks. */
  readonly placeAnywhere?: boolean;
  /**
   * A two-cell piece. "h" occupies the anchor and the square to its right
   * (horizontal, e.g. a Valley); "v" the anchor and the square above it
   * (vertical, e.g. a Ridge). Both cells must be empty + on-board to deploy.
   */
  readonly footprint?: "h" | "v";
  /** False = can never be captured by either side (terrain). Defaults to true. */
  readonly capturable?: boolean;
  /** Plain-language explanation of how the piece moves (shown on hover / in the deck builder). */
  readonly description?: string;
  /**
   * Max copies allowed in a single deck. Powerful pieces are capped (to stop a
   * deck stacking, say, five queens); cheap fodder (pawns, farmers, lances) is
   * left uncapped. Undefined = no limit.
   */
  readonly maxCopies?: number;
  /**
   * Optional teaching-label override (see `CardTier`). Omit to let `cardTier`
   * derive it from cost (the common case); set it only when a piece's role
   * differs from what its price implies.
   */
  readonly tier?: CardTier;
}

export const PIECES: Readonly<Record<PieceTypeId, PieceDef>> = {
  pawn: {
    id: "pawn",
    name: "Pawn",
    cost: 1,
    value: 1,
    glyph: "♟",
    description:
      "Moves forward one square — two from its starting rank — and captures one square diagonally forward. Promotes to a Queen on the far rank.",
    movement: pawnMovement,
    deployable: true,
    promotesTo: "queen",
    maxCopies: 8,
  },
  farmer: {
    id: "farmer",
    name: "Farmer",
    cost: 1,
    value: 1,
    glyph: "▼",
    description:
      "An inverse pawn: steps one square diagonally forward to an empty square, and captures one square straight ahead. Promotes to a Queen on the far rank.",
    // Inverse pawn: steps diagonally forward, captures straight ahead.
    movement: farmerMovement,
    deployable: true,
    promotesTo: "queen",
    maxCopies: 6,
  },
  lance: {
    id: "lance",
    name: "Lance",
    cost: 2,
    value: 2,
    glyph: "▲",
    description:
      "Slides straight forward any number of squares (shogi-style). It can never move sideways or retreat.",
    // Slides straight forward any distance; can never retreat (shogi-style).
    // Intentionally has no promotion — a Lance that reaches the far rank is
    // dead, which is the trade-off for its long one-way reach.
    movement: forwardSlider([0]),
    deployable: true,
    maxCopies: 6,
  },
  knight: {
    id: "knight",
    name: "Knight",
    cost: 3,
    value: 3,
    glyph: "♞",
    description:
      "Jumps in an L-shape (two squares one way, then one square at a right angle), leaping over any pieces in between.",
    movement: leaper(KNIGHT_OFFSETS),
    deployable: true,
    maxCopies: 2,
  },
  camel: {
    id: "camel",
    name: "Camel",
    cost: 3,
    value: 3,
    glyph: "♞",
    description:
      "A long-range Knight: leaps in an extended L — three squares one way and one the other — jumping over anything in between.",
    movement: leaper(CAMEL_OFFSETS),
    deployable: true,
    maxCopies: 2,
  },
  bishop: {
    id: "bishop",
    name: "Bishop",
    cost: 3,
    value: 3,
    glyph: "♝",
    description: "Slides any number of squares diagonally, until blocked.",
    movement: slider(DIAGONAL),
    deployable: true,
    maxCopies: 2,
  },
  rook: {
    id: "rook",
    name: "Rook",
    cost: 5,
    value: 5,
    glyph: "♜",
    description: "Slides any number of squares straight — along a rank or file — until blocked.",
    movement: slider(ORTHOGONAL),
    deployable: true,
    maxCopies: 2,
  },
  outpost: {
    id: "outpost",
    name: "Outpost",
    cost: 3,
    value: 2,
    glyph: "◆",
    description:
      "Moves one square in any direction. Each turn it projects a deployment node onto a random adjacent square — you may deploy a piece there, beyond your home ranks. It can be captured like any piece.",
    movement: leaper(ALL_DIRECTIONS),
    deployable: true,
    maxCopies: 2,
  },
  queen: {
    id: "queen",
    name: "Queen",
    cost: 9,
    value: 9,
    glyph: "♛",
    description: "Slides any number of squares in any direction — straight or diagonal. The most powerful piece.",
    movement: slider(ALL_DIRECTIONS),
    deployable: true,
    // Capped at one: the Queen is the strongest piece, so a single copy keeps
    // a deck from snowballing on raw material. (Pawn promotion can still mint
    // more Queens on the board — that limit is on deck-building only.)
    maxCopies: 1,
  },
  king: {
    id: "king",
    name: "King",
    cost: 0,
    value: 0,
    glyph: "♚",
    description:
      "Moves one square in any direction. While your Prince is alive the King is fully immune — no check or capture. Once the Prince falls, checkmate the King to win (and avoid being mated yourself).",
    movement: leaper(ALL_DIRECTIONS),
    royal: "king",
    deployable: false,
  },
  prince: {
    id: "prince",
    name: "Prince",
    cost: 0,
    value: 0,
    glyph: "❈",
    description:
      "Moves one square in any direction, like the King. It can be captured freely — but while it lives, it makes your King completely immune. Guard it.",
    movement: leaper(ALL_DIRECTIONS),
    royal: "prince",
    deployable: false,
  },
  hill: {
    id: "hill",
    name: "Mound",
    cost: 3,
    value: 0,
    glyph: "⛰",
    description:
      "Terrain, not a fighter. Deploy it onto ANY empty square; it can never move or be captured, and it permanently blocks that square for both sides.",
    // Immovable terrain: it has no moves and can never be captured. It can be
    // deployed onto any empty square and permanently blocks that square for
    // both sides.
    movement: noMovement,
    deployable: true,
    placeAnywhere: true,
    capturable: false,
    maxCopies: 2,
  },
  valley: {
    id: "valley",
    name: "Hills",
    cost: 4,
    value: 0,
    glyph: "⩊",
    description:
      "Wide terrain: covers TWO horizontally-adjacent squares. Deploy it onto any two empty side-by-side squares; it never moves or is captured, and permanently blocks both squares for everyone.",
    movement: noMovement,
    deployable: true,
    placeAnywhere: true,
    footprint: "h",
    capturable: false,
    maxCopies: 2,
  },
  ridge: {
    id: "ridge",
    name: "Walls",
    cost: 4,
    value: 0,
    glyph: "⩉",
    description:
      "Tall terrain: covers TWO vertically-adjacent squares. Deploy it onto any two empty stacked squares; it never moves or is captured, and permanently blocks both squares for everyone.",
    movement: noMovement,
    deployable: true,
    placeAnywhere: true,
    footprint: "v",
    capturable: false,
    maxCopies: 2,
  },
  captain: {
    id: "captain",
    name: "Captain",
    cost: 6,
    value: 4,
    glyph: "✦",
    description:
      "Moves one square in any direction. Each turn it gets one FREE move that doesn't use your action — you can move the Captain and still deploy, draw, or move another piece. (Max 1, and its move still can't leave your King in check.)",
    movement: leaper(ALL_DIRECTIONS),
    deployable: true,
    maxCopies: 1,
  },
};

/**
 * Every piece a player can buy into a deck — derived from the `deployable` flag
 * so it can never fall out of sync with the registry (it previously hard-coded a
 * list that silently omitted the Captain and the two-cell terrain). Order
 * follows the registry; the deck builder lays cards out by pack, not from this.
 */
export const DEPLOYABLE_PIECES: readonly PieceDef[] = Object.values(PIECES).filter(
  (def) => def.deployable,
);

export function pieceDef(type: PieceTypeId): PieceDef {
  return PIECES[type];
}
